// utils
import { getWidget } from "../shared/utils"
import { study_identification } from "../../dataSchemas/protocol/fieldSchemas"
import { jrctDataSchema } from "../../dataSchemas/protocol/jrctDataSchema"
import { ctgProtocolUISchema } from "./ctgProtocolUISchema"
import { setHiddenFields, hideStudyDetailsKeys, getPreviewStyles } from "./utils/utils"
import { protocolClassNames, firstProtocolSectionClassNames, objectClassNames, arrayClassNames } from "../classes"

const hideFields = {
  root: [
    "type_of_research",
    "study_type",
    "eudract_number",
    "contact_information",
    "study_oversight",
    "study_arms",
    "investigator_information",
    "non_investigator_information",
    "information_on_multicenter_clinical_trials",
    "overview_of_pharmaceuticals",
    "monetary_sponsor",
    "irb_information",
    "ema_hma_additional_information",
  ],
  study_identification: {
    root: [
      "registered_in_jrct_registry",
      "summary_of_changes",
      "country_regulator_not_listed",
      "eupas_registry_number",
      "darwin_eu_study",
      "study_topic",
      "study_topic_other",
      "emarwd_official_title_and_acronym",
      "other_study_ids",
      "regulatory_procedure_number",
    ],
  },
  study_details: {
    milestone_dates: {
      root: ["fpi", "pcd"],
      lpo: {
        root: ["baseline", "actual"],
      },
    },
  },
  study_timeline: {
    root: [
      "signed_funding_contract_date",
      // "data_collection_start_date",
      "data_analysis_start_date",
      "interim_report_date",
      "final_study_report_date",
      "record_verification_date",
      "recruitment",
      "expanded",
      "plan_sponsor_audit",
      "more_details_sources_of_funding",
      "compensation_and_medical_care_in_the_event_of_health_damage",
    ],
    milestone_dates: {
      root: ["study_start_date", "primary_completion_date", "study_completion_date"],
    },
    funding_source: {
      root: ["pharmaceutical_companies", "charities", "government_body", "research_councils", "eu_funding_scheme"],
      other_funding_sources: {
        root: ["funding"],
      },
    },
  },
  study_description: {
    root: [
      "study_keywords",
      "detailed_description",
      "study_keywords",
      "study_conditions",
      "additional_medical_conditions",
      "start_date_of_enrollment",
      "study_start_date_in_japan",
      "inclusion_criteria",
      "exclusion_criteria",
      "discontinuation_criteria",
      "target_disease_code",
      "study_keywords",
      "presence_of_interventions",
      "interventions",
      "intervention_code",
      "intervention_keywords",
      "primary_outcome_measures",
      "secondary_outcome_measures",
    ],
    products: {
      root: ["arms", "interventions", "crossref"],
    },
  },
  study_scope: {
    root: [
      "patient_registry",
      "primary_purpose",
      "study_phase",
      "interventional_study_model",
      "model_description",
      "number_of_arms",
      "masking",
      "masking_description",
      "jrct_masking",
      "study_type",
      "allocation",
      "enrollment_type",
      "enrollment_type_obs",
      "patient",
      "follow_up_duration",
      "duration_type",
      "additional_patient_registry_data",
      "observational_study_model",
      "time_perspective",
      "biospecimen",
      "number_of_cohorts",
      "selection",
      "scope_of_study",
      "other_scope_description",
      "design_of_study",
      "other_non_interventional_study_design",
      "control",
      "placebo",
      "blinding",
      "randomization",
      "non_insurance_combined_medical_expenses",
      "countries_of_recruitment_except_japan",
      "clinical_trial_regulatory_scope",
      "clinical_trial_type",
      "data_collection_method",
      "summary_results",
    ],
  },
  study_ipd: {
    root: [
      "ipd_sharing_info",
      "time_frame",
      "access_criteria",
      "url_text",
      "contents_of_informed_consent_for_study",
      "other_registries",
      "notes",
      "supplementary_information",
    ],
  },
  study_outcome_measures: {
    root: ["outcome_measures", "statistical_analysis_plan", "eupas_outcomes"],
  },
  study_eligibilty: {
    root: [
      "gender_based",
      "age_limits",
      "accept_healthy_volunteers",
      "is_clinical_study",
      "eligibility_criteria",
      "study_population_description",
      "sampling_method",
      "sources_of_data",
      "registered_data_sources",
      "other_registered_data_sources",
      "other_data_source",
      "short_description_of_study_population",
      "age_groups",
      "special_interest_of_population",
      "other_special_interest_of_population",
    ],
    population_under_study: {
      root: ["age", "sex", "other_population"],
    },
  },
  study_reference: {
    root: [
      "citations",
      "links",
      "available",
      "is_requesting_encepp_seal",
      // "make_full_protocol_public_when_study_ends",
      "encepp_code_of_conduct",
    ],
    study_publications: {
      root: ["url"],
    },
  },
}

const showFields = {
  study_identification: [
    "brief_title",
    "brief_title_translation",
    "official_title",
    "official_title_translation",
    "secondary_ids",
  ],
}

const ctgSchema = ctgProtocolUISchema()

export const ctisProtocolUISchema = (configs) => {
  let preview = configs?.preview
  let formData = configs?.formData
  const disableValidation = configs?.disableValidation || false

  let returnObj = {
    "ui:options": {
      classNames: {
        objectContainer: "rjs-full",
        field: "rjs-full",
      },
    },
    protocol: {
      "ui:order": [
        "protocol_id",
        "study_type",
        "study_identification",
        "secondary_attributes",
        "milestone_dates",
        "study_timeline",
        "ctis_form",
        "mscs",
        "study_description",
        "study_eligibilty",
        "protocol_information",
        "contact_information",
        "study_reference",
        "study_scope",
        "sponsors",
        "study_arms",
        "*",
      ],
      "ui:options": {
        title: "Study Identification and Information",
        classNames: protocolClassNames,
      },
      protocol_id: {
        "ui:options": {
          label: false,
          title: "Protocol code",
          placeholder: "Enter protocol code",
          disabled: true,
          classNames: {
            field: "rjs-col-lg",
          },
        },
      },
      mscs: {
        "ui:options": {
          title: "MSCs",
          classNames: objectClassNames.primary,
        },
        member_states_concerned: {
          "ui:options": {
            title: "Member States Concerned",
            classNames: objectClassNames.tertiary,
          },
          member_states_concerned_table: {
            "ui:options": {
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "MemberStatesConcerned", preview }),
          },
        },
      },
      study_identification: {
        "ui:order": [
          "brief_title",
          "brief_title_translation",
          "official_title",
          "official_title_translation",
          "acronym",
          "protocol_id",
          "secondary_ids",
          "*",
        ],
        "ui:options": {
          classNames: firstProtocolSectionClassNames,
        },
        brief_title: {
          "ui:options": {
            label: false,
            title: "Full Title",
            placeholder: "Enter Full Title",
            tooltip: "Full scientific title of the clinical trial as specified in the study protocol submitted.",
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        brief_title_translation: {
          "ui:options": {
            title: "Full Title Translation",
            itemTitle: "Translation",
            addButtonTitle: "Add Translation",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            language: {
              "ui:options": {
                title: "Language",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            translation: {
              "ui:options": {
                title: "Translation",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-2-3",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        official_title: {
          "ui:options": {
            label: false,
            title: "Public Title",
            placeholder: "Enter Official Title",
            tooltip: "Full scientific title of the clinical trial as specified in the study protocol submitted.",
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        official_title_translation: {
          "ui:options": {
            title: "Public Title Translation",
            itemTitle: "Translation",
            addButtonTitle: "Add Translation",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            language: {
              "ui:options": {
                title: "Language",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            translation: {
              "ui:options": {
                title: "Translation",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-2-3",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        secondary_ids: {
          ...ctgSchema.protocol.study_identification.secondary_ids,
          items: {
            ...ctgSchema.protocol.study_identification.secondary_ids.items,
            secondary_id_type: {
              ...ctgSchema.protocol.study_identification.secondary_ids.items.secondary_id_type,
              "ui:options": {
                ...ctgSchema.protocol.study_identification.secondary_ids.items.secondary_id_type["ui:options"],
                validations: [
                  {
                    regex:
                      /^(?!US NIH Grant\/Contract Award Number$|Other Grant\/Funding Number$|Registry Identifier$|EU Trial \(CTIS\) Number$|EudraCT Number$|Other Identifier$).*/,
                    message: "This ID type only exists in CTG",
                    type: "NOTE",
                  },
                ],
              },
            },
          },
        },
        ...setHiddenFields(
          Object.keys({
            ...study_identification.properties,
            ...jrctDataSchema.study_identification.clinical_trial_post_marketing_surveillance,
            ...jrctDataSchema.study_identification.shared,
          }),
          showFields.study_identification
        ),
        ...setHiddenFields(hideFields.study_identification.root),
      },
      ctis_form: {
        "ui:options": {
          title: "Form",
          classNames: objectClassNames.primary,
        },
        deferral_clinical_trial_information: {
          "ui:order": [
            "short_title",
            "justification_for_trial_category",
            "main_characteristics",
            "notifications",
            "subject_information_sheet",
            "subject_information_sheet_years",
            "subject_information_sheet_months",
            "protocol_summary_scientific_advice",
            "protocol_summary_scientific_advice_years",
            "protocol_summary_scientific_advice_months",
            "impd_sande_sections_investigator_brochure",
            "impd_sande_sections_investigator_brochure_years",
            "impd_sande_sections_investigator_brochure_months",
            "responses_rfi",
            "responses_rfi_years",
            "responses_rfi_months",
            "clinical_trial_results_summary_intermediate_data_analysis",
            "clinical_trial_results_summary_intermediate_data_analysis_months",
            "clinical_trial_results_summary_lay_person_summary",
            "clinical_trial_results_summary_lay_person_summary_months",
            "*",
          ],
          "ui:options": {
            title: "Deferral of clinical trial information",
            classNames: objectClassNames.tertiary,
          },
          short_title: {
            "ui:options": {
              title: "Short title / Trial category",
              tooltip: `Category applied to the trial that will determine the publishing rules. More information can be consulted on https://www.ema.europa.eu/en/documents/other/appendix-disclosure-rules-functional-specifications-eu-portal-eu-database-be-audited_en.pdf. NOTE: The action of updating from Category 1 or Catergory 2 to Category 3 will reset the publication dates to the default 'Date of decision' for that category and will also remove any deferrals`,
              label: false,
              log: true,
              classNames: {
                fieldContainer: "rjs-col-xl",
                field: "rjs-col-md",
              },
            },
          },
          justification_for_trial_category: {
            "ui:options": {
              title: "Justification for trial category / Trial category",
              tooltip:
                "Category applied to the trial that will determine the publishing rules. More information can be consulted on https://www.ema.europa.eu/en/documents/other/appendix-disclosure-rules-functional-specifications-eu-portal-eu-database-be-audited_en.pdf",
              placeholder: "",
              label: false,
              log: true,
              classNames: {
                fieldContainer: "rjs-col-2xl",
              },
            },
          },
          main_characteristics: {
            "ui:options": {
              title: "Main characteristics",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": "radio",
          },
          notifications: {
            "ui:options": {
              title: "Notifications",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": "radio",
          },
          subject_information_sheet: {
            "ui:options": {
              title: "Subject information sheet",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
                field: "rjs-col-1-2",
              },
            },
            "ui:widget": "radio",
          },
          protocol_summary_scientific_advice: {
            "ui:options": {
              title: "Protocol and Summary of Scientific Advice",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
                field: "rjs-col-1-2",
              },
            },
            "ui:widget": "radio",
          },
          impd_sande_sections_investigator_brochure: {
            "ui:options": {
              title: "IMPD SandE sections and Investigator Brochure",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
                field: "rjs-col-1-2",
              },
            },
            "ui:widget": "radio",
          },
          responses_rfi: {
            "ui:options": {
              title: "Responses to RFI",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
                field: "rjs-col-1-2",
              },
            },
            "ui:widget": "radio",
          },
          clinical_trial_results_summary_intermediate_data_analysis: {
            "ui:options": {
              title: "Clinical trial results summary for an intermediate data analysis",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": "radio",
          },
          clinical_trial_results_summary_lay_person_summary: {
            "ui:options": {
              title: "Clinical trial results summary and lay person summary",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": "radio",
          },
          subject_information_sheet_years: {
            "ui:options": {
              title: "Years",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          subject_information_sheet_months: {
            "ui:options": {
              title: "Months after the end of trial",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          protocol_summary_scientific_advice_years: {
            "ui:options": {
              title: "Years",
              type: "number",
              placeholder: "--Select--",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          protocol_summary_scientific_advice_months: {
            "ui:options": {
              title: "Months after the end of trial",
              type: "number",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          impd_sande_sections_investigator_brochure_years: {
            "ui:options": {
              title: "Years",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          impd_sande_sections_investigator_brochure_months: {
            "ui:options": {
              title: "Months after the end of trial",
              placeholder: "--Select--",
              type: "number",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          responses_rfi_years: {
            "ui:options": {
              title: "Years",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          responses_rfi_months: {
            "ui:options": {
              title: "Months after the end of trial",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          clinical_trial_results_summary_intermediate_data_analysis_months: {
            "ui:options": {
              title: "Months after the end of trial",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          clinical_trial_results_summary_lay_person_summary_months: {
            "ui:options": {
              title: "Months after the end of trial",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
          },
          justification_deferral_published_decision: {
            "ui:options": {
              title: "Justification for deferral published at decision",
              placeholder: "--Select--",
              label: false,
              type: "number",
              classNames: {
                fieldContainer: "rjs-col-1-4",
              },
            },
            "ui:widget": "textarea",
          },
        },
      },
      study_outcome_measures: {
        "ui:options": {
          title: "Study Endpoints / Outcome Measures",
          classNames: objectClassNames.primary,
        },
        outcome_measures_ctis: {
          "ui:options": {
            title: "Outcome Measures",
            addButtonTitle: "Add Outcome Measure",
            arrayItemTitle: "Outcome Measure",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            customFnProps: [
              {
                tooltip: "Copy and consolidate outcome measures from CTG Protocol",
                fnKey: "copy-ctg-protocol-om-to-ctis",
              },
            ],
            showIndex: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            title: {
              "ui:options": {
                title: "Title",
                placeholder: "Enter Title",
                hideFork: true,
                label: false,
                rows: 1,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            type: {
              "ui:options": {
                title: "Type",
                label: false,
                hideFork: true,
                required: true,
                disableValidation,
                orientation: "horizontal",
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            translations: {
              "ui:options": {
                title: "Translations",
                itemTitle: "Translation",
                addButtonTitle: "Add Translation",
                label: false,
                addable: true,
                removable: true,
                orderable: true,
                classNames: arrayClassNames.primary,
              },
              items: {
                language: {
                  "ui:options": {
                    title: "Language",
                    hideFork: true,
                    label: false,
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                translation: {
                  "ui:options": {
                    title: "Translation",
                    label: false,
                    hideFork: true,
                    classNames: {
                      fieldContainer: "rjs-full",
                    },
                  },
                  "ui:widget": getWidget({ type: "textarea", preview }),
                },
              },
            },
          },
        },
        ...setHiddenFields(hideFields.study_outcome_measures.root),
      },
      study_timeline: {
        "ui:options": {
          classNames: {
            objectContainer: "pb-24",
            objectContainer: "rjs-full",
          },
        },
        milestone_dates: {
          "ui:order": ["estimated_recruitment_start_date_eea", "primary_completion_date", "study_completion_date", "*"],
          "ui:options": {
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          estimated_recruitment_start_date_eea: {
            "ui:options": {
              title: "Estimated recruitment start date in EEA",
              classNames: objectClassNames.dateXl,
            },
            date: {
              "ui:options": {
                label: false,
                hideComment: true,
                title: "Date",
                classNames: {
                  fieldContainer: "rjs-col-date",
                },
              },
              "ui:widget": getWidget({ type: "date", preview }),
            },
          },
          ...setHiddenFields(hideFields.study_timeline.milestone_dates.root),
        },
        other_funding_sources: {
          "ui:options": {
            title: "Other sources (Organization names)",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
              field: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "SelectTag", preview }),
        },
        ...setHiddenFields(hideFields.study_timeline.root),
      },
      study_description: {
        "ui:options": {
          title: "Trial Information (Part 1)",
          classNames: objectClassNames.primary,
        },
        "ui:order": [
          "low_intervention_trial",
          "justification_for_low_intervention_trial",
          "ctis_trial_phase",
          "medical_conditions",
          "therapeutic_area",
          "trial_scope",
          "brief_summary",
          "brief_summary_translations",
          "secondary_objective",
          "secondary_objective_translations",
          "meddra",
          "principle_inclusion_criteria",
          "principle_exlusion_criteria",
          "*",
        ],
        trial_scope: {
          "ui:options": {
            title: "Trial Scope",
            label: false,
            orientation: "vertical",
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        ctis_trial_phase: {
          "ui:options": {
            title: "Trial Phase",
            hideFork: true,
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        brief_summary: {
          "ui:options": {
            title: "Main objective (English)",
            placeholder: "Enter main objective",
            tooltip: "Full scientific title of the clinical trial as specified in the study protocol submitted.",
            errorMessage: "Please specify all required information.",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        brief_summary_translations: {
          "ui:options": {
            title: "Main Objective Languages",
            itemTitle: "Translation",
            addButtonTitle: "Add Translation",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            language: {
              "ui:options": {
                title: "Language",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            translation: {
              "ui:options": {
                title: "Translation",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-2-3",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        secondary_objective: {
          "ui:options": {
            title: "Secondary objective (English)",
            placeholder: "Enter secondary objective",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        secondary_objective_translations: {
          "ui:options": {
            title: "Secondary Objectives Translation",
            itemTitle: "Translation",
            addButtonTitle: "Add Translation",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            language: {
              "ui:options": {
                title: "Language",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            translation: {
              "ui:options": {
                title: "Translation",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-2-3",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        low_intervention_trial: {
          "ui:options": {
            title: "Low intervention trial",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
              field: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
        },
        justification_for_low_intervention_trial: {
          "ui:options": {
            title:
              "Justification for the low intervention trial* Attachment of justification of low interventional clinical trial",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        medical_conditions: {
          "ui:options": {
            title: "Medical Conditions",
            addButtonTitle: "Add Condition",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            condition: {
              "ui:options": {
                title: "Medical condition(s) (English)",
                required: true,
                hideFork: true,
                label: false,
                disableValidation,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            translations: {
              "ui:options": {
                title: "Translations",
                itemTitle: "Translation",
                addButtonTitle: "Add Translation",
                label: false,
                hideFork: true,
                addable: true,
                removable: true,
                orderable: true,
                classNames: arrayClassNames.primary,
              },
              items: {
                language: {
                  "ui:options": {
                    title: "Language",
                    label: false,
                    hideFork: true,
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                translation: {
                  "ui:options": {
                    title: "Translation",
                    label: false,
                    hideFork: true,
                    classNames: {
                      fieldContainer: "rjs-full",
                    },
                  },
                  "ui:widget": getWidget({ type: "textarea", preview }),
                },
              },
            },
            is_medical_condition_rare: {
              "ui:options": {
                title: "Is the medical condition considered to be a rare disease?",
                label: false,
                hideFork: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
            },
          },
        },
        therapeutic_area: {
          "ui:options": {
            title: "CTIS Therapeutic area",
            label: false,
            orientation: "vertical",
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        meddra: {
          "ui:options": {
            title: "MedDRA",
            addButtonTitle: "Add MedDRA",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            term_name: {
              "ui:options": {
                title: "Term Name",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            level: {
              "ui:options": {
                title: "Level",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            classification_code: {
              "ui:options": {
                title: "Classification Code",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
          },
        },
        principle_inclusion_criteria: {
          "ui:options": {
            title: "Principle inclusion criteria",
            addButtonTitle: "Add inclusion criteria",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            inclusion_criteria: {
              "ui:options": {
                title: "Inclusion criteria (English)",
                label: false,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
            translations: {
              "ui:options": {
                title: "Translations",
                itemTitle: "Translation",
                addButtonTitle: "Add Translation",
                arrayItemTitle: "Translation",
                showIndex: true,
                label: false,
                addable: true,
                removable: true,
                orderable: true,
                classNames: arrayClassNames.primary,
              },
              items: {
                language: {
                  "ui:options": {
                    title: "Language",
                    label: false,
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                translation: {
                  "ui:options": {
                    title: "Translation",
                    label: false,
                    classNames: {
                      fieldContainer: "rjs-col-2-3",
                    },
                  },
                  "ui:widget": getWidget({ type: "textarea", preview }),
                },
              },
            },
          },
        },
        principle_exlusion_criteria: {
          "ui:options": {
            title: "Principle exclusion criteria",
            itemTitle: "Translation",
            addButtonTitle: "Add exclusion criteria",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            exclusion_criteria: {
              "ui:options": {
                title: "Exclusion criteria (English)",
                label: false,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
            translations: {
              "ui:options": {
                title: "Translations",
                itemTitle: "Translation",
                addButtonTitle: "Add Translation",
                arrayItemTitle: "Translation",
                showIndex: true,
                label: false,
                addable: true,
                removable: true,
                orderable: true,
                classNames: arrayClassNames.primary,
              },
              items: {
                language: {
                  "ui:options": {
                    title: "Language",
                    label: false,
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                translation: {
                  "ui:options": {
                    title: "Translation",
                    label: false,
                    classNames: {
                      fieldContainer: "rjs-col-2-3",
                    },
                  },
                  "ui:widget": getWidget({ type: "textarea", preview }),
                },
              },
            },
          },
        },
        protocol_information: {
          "ui:options": {
            title: "Protocol Information",
            classNames: objectClassNames.tertiary,
          },
          periods: {
            "ui:options": {
              title: "Periods",
              itemTitle: "Period",
              addButtonTitle: "Add Period",
              label: false,
              addable: true,
              removable: true,
              orderable: true,
              classNames: arrayClassNames.secondary,
            },
            items: {
              title: {
                "ui:options": {
                  title: "Title",
                  placeholder: "",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              description: {
                "ui:options": {
                  title: "Description",
                  placeholder: "",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "textarea", preview }),
              },
              allocation_method: {
                "ui:options": {
                  title: "Allocation method",
                  placeholder: "",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              binding_used: {
                "ui:options": {
                  title: "Blinding used",
                  placeholder: "",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              roles_blinded: {
                "ui:options": {
                  title: "Roles blinded",
                  placeholder: "",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
                "ui:widget": getWidget({ type: "MultiSelect", preview }),
              },
              blinding_implementation_details: {
                "ui:options": {
                  title: "Blinding implementation details",
                  placeholder: "",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "textarea", preview }),
              },
              arms: {
                "ui:options": {
                  title: "Arms",
                  itemTitle: "Arm",
                  addButtonTitle: "Add Arm",
                  label: false,
                  arrayItemTitle: "Arm",
                  addable: true,
                  removable: true,
                  orderable: true,
                  showIndex: true,
                  classNames: arrayClassNames.primary,
                },
                items: {
                  title: {
                    "ui:options": {
                      label: false,
                      title: "Title",
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  description: {
                    "ui:options": {
                      label: false,
                      title: "Description",
                      classNames: {
                        fieldContainer: "rjs-full",
                      },
                    },
                    "ui:widget": getWidget({ type: "textarea", preview }),
                  },
                },
              },
            },
          },
        },
        scientific_advice_and_pip: {
          "ui:options": {
            title: "Scientific advice and Paediatric Investigation Plan (PIP)",
            classNames: objectClassNames.tertiary,
          },
          "ui:order": ["investigator_contact_details", "scientific_advice", "*"],
          scientific_advice: {
            "ui:options": {
              title: "Scientific advice",
              addButtonTitle: "Add competent authority",
              label: false,
              addable: true,
              removable: true,
              orderable: true,
              classNames: arrayClassNames.secondary,
            },
            items: {
              "ui:options": {
                classNames: objectClassNames.contact_root_container,
              },
              name: {
                "ui:options": {
                  title: "Name",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              id: {
                "ui:options": {
                  title: "ID",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              city: {
                "ui:options": {
                  title: "City",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              country: {
                "ui:options": {
                  title: "Country",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              address: {
                "ui:options": {
                  title: "Address",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              postcode: {
                "ui:options": {
                  title: "Postcode",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              phone: {
                "ui:options": {
                  title: "Phone",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              email: {
                "ui:options": {
                  title: "Email",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
          },
          paediatric_investigation_plan: {
            "ui:options": {
              title: "Paediatric investigation plan",
              addButtonTitle: "Add PIP",
              label: false,
              addable: true,
              removable: true,
              orderable: true,
              classNames: arrayClassNames.secondary,
            },
            items: {
              ema_paediatric_investigation_number: {
                "ui:options": {
                  title: "EMA paediatric investigation number",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
          },
        },
        associated_clinical_trials: {
          "ui:options": {
            title: "Associated clinical trials",
            classNames: objectClassNames.tertiary,
          },
          associated_clinical_trials: {
            "ui:options": {
              // title: "Associated clinical trials",
              itemTitle: "Identifier",
              addButtonTitle: "Associate clinical trial",
              label: false,
              addable: true,
              removable: true,
              orderable: true,
              classNames: arrayClassNames.secondary,
            },
            items: {
              identifier: {
                "ui:options": {
                  title: "Identifier",
                  label: false,
                  hideFork: true,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              full_title: {
                "ui:options": {
                  title: "Full title",
                  label: false,
                  hideFork: true,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "textarea", preview }),
              },
            },
          },
        },
        sponsors: {
          "ui:options": {
            title: "Sponsors",
            classNames: {
              objectTitle: "rjs-h2",
            },
          },
          sponsors: {
            "ui:options": {
              itemTitle: "Sponsor",
              addButtonTitle: "Add Sponsor",
              label: false,
              addable: true,
              removable: true,
              orderable: true,
              classNames: arrayClassNames.primary,
            },
            items: {
              "ui:order": [
                "sponsor",
                "add_legal_representative",
                "legal_representative",
                "add_scientific_contact_point",
                "scientific_contact_point",
                "add_public_contact_point",
                "public_contact_point",
                "*",
              ],
              sponsor: {
                "ui:options": {
                  title: "Sponsor",
                  classNames: {
                    objectTitle: "rjs-h3",
                  },
                },
                organisation_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Name",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                status: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Status",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                address: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Address",
                    classNames: {
                      fieldContainer: "rjs-col-xl",
                    },
                  },
                },
                city: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Town/City",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                zip_code: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Post code",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
                country: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Country",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                email: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Email",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
                phone: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Phone",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
              },
              add_legal_representative: {
                "ui:options": {
                  title: "Add legal representative",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
              },
              legal_representative: {
                "ui:options": {
                  title: "Legal representative",
                  classNames: {
                    objectTitle: "rjs-h3",
                  },
                },
                organisation_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Name",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                status: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Status",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                address: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Address",
                    classNames: {
                      fieldContainer: "rjs-col-xl",
                    },
                  },
                },
                city: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Town/City",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                zip_code: {
                  "ui:options": {
                    label: false,
                    required: true,
                    title: "Post code",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
                country: {
                  "ui:options": {
                    label: false,
                    required: true,
                    title: "Country",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                email: {
                  "ui:options": {
                    label: false,
                    required: true,
                    title: "Email",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
                phone: {
                  "ui:options": {
                    label: false,
                    required: true,
                    title: "Phone",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
                contact: {
                  "ui:options": {
                    title: "Contact",
                    classNames: {
                      objectTitle: "rjs-h3",
                    },
                  },
                  first_name: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "First Name",
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  last_name: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Last Name",
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  email: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Email",
                      classNames: {
                        fieldContainer: "rjs-col-md",
                      },
                    },
                  },
                  phone: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Phone",
                      classNames: {
                        fieldContainer: "rjs-col-md",
                      },
                    },
                  },
                },
              },
              add_scientific_contact_point: {
                "ui:options": {
                  title: "Add scientific contact point",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
              },
              scientific_contact_point: {
                "ui:options": {
                  title: "Scientific contact point",
                  classNames: {
                    objectTitle: "rjs-h3",
                  },
                },
                organisation_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Name",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                functional_contact_point_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Status",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                email: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Email",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
                phone: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Phone",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
              },
              add_public_contact_point: {
                "ui:options": {
                  title: "Add public contact point",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
              },
              public_contact_point: {
                "ui:options": {
                  title: "Public contact point",
                  classNames: {
                    objectTitle: "rjs-h3",
                  },
                },
                organisation_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Name",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                functional_contact_point_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Status",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                email: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Email",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
                phone: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Phone",
                    classNames: {
                      fieldContainer: "rjs-col-md",
                    },
                  },
                },
              },
              third_party: {
                "ui:options": {
                  title: "Third party",
                  itemTitle: "Sponsor",
                  addButtonTitle: "Add third party",
                  label: false,
                  addable: true,
                  removable: true,
                  orderable: true,
                  classNames: arrayClassNames.primary,
                },
                items: {
                  organisation_name: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Name",
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  country: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Country",
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  address: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Address",
                      classNames: {
                        fieldContainer: "rjs-col-xl",
                      },
                    },
                  },
                  city: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Town/City",
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  zip_code: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Post code",
                      classNames: {
                        fieldContainer: "rjs-col-md",
                      },
                    },
                  },
                  phone: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Phone",
                      classNames: {
                        fieldContainer: "rjs-col-md",
                      },
                    },
                  },
                  email: {
                    "ui:options": {
                      label: false,
                      required: true,
                      disableValidation,
                      title: "Email",
                      classNames: {
                        fieldContainer: "rjs-col-md",
                      },
                    },
                  },
                  duties: {
                    "ui:options": {
                      title: "Duties",
                      label: false,
                      orientation: "vertical",
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                    "ui:widget": getWidget({ type: "MultiSelect", preview }),
                  },
                  other_duties: {
                    "ui:options": {
                      title: "Please explain if other duties: ",
                      label: false,
                      orientation: "vertical",
                      classNames: {
                        fieldContainer: "rjs-full",
                      },
                    },
                    "ui:widget": getWidget({ type: "textarea", preview }),
                  },
                },
              },
            },
          },
          contact_point_for_union: {
            "ui:options": {
              title: "Contact point for union",
              classNames: objectClassNames.contact_root_container,
            },
            organisation_name: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Name",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            address_1: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Address 1",
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            address_2: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Address 2",
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            address_3: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Address 3",
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            address_4: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Address 4",
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            city: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Town/City",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            zip_code: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Post code",
                classNames: {
                  fieldContainer: "rjs-col-md",
                },
              },
            },
            country: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Country",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            functional_contact_point_name: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Functional contact point name",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            contact: {
              "ui:options": {
                title: "Contact",
                classNames: {
                  objectTitle: "rjs-h2",
                },
              },
              first_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "First name",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              last_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Last name",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              phone: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Phone",
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              email: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Email",
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
            },
          },
        },
        products: {
          "ui:options": {
            title: "Products",
            classNames: {
              fieldContainer: "rjs-full",
              objectTitle: "rjs-h2",
            },
          },
          products: {
            "ui:options": {
              arrayItemTitle: "Role",
              addButtonTitle: "Add Role",
              showIndex: true,
              label: false,
              addable: true,
              removable: true,
              orderable: true,
              classNames: arrayClassNames.primary,
            },
            items: {
              "ui:order": [
                "role",
                "description",
                "authorisation_number_manufacturing_and_import",
                "quality_justification_for_no_impd_upload",
                "safety_and_efficacy_justification_for_no_impd_upload",
                "reason_for_no_ampd_upload",
                "products",
                "*",
              ],
              role: {
                "ui:options": {
                  title: "Role",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              products: {
                "ui:options": {
                  title: "Products",
                  addButtonTitle: "Add Product",
                  arrayItemTitle: "Product",
                  showIndex: true,
                  label: false,
                  addable: true,
                  removable: true,
                  orderable: true,
                  classNames: arrayClassNames.secondary,
                },
                items: {
                  product: {
                    "ui:options": {
                      title: "Product",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  eu_mp_number: {
                    "ui:options": {
                      title: "EU MP Number",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  pharmaceutical_form: {
                    "ui:options": {
                      title: "Pharmaceutical form",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-xl",
                      },
                    },
                  },
                  marketing_authorisation_number: {
                    "ui:options": {
                      title: "Marketing authorisation number",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  strength: {
                    "ui:options": {
                      title: "Strength",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  name_of_product: {
                    "ui:options": {
                      title: "Name of product",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  active_substance_name: {
                    "ui:options": {
                      title: "Active substance name",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  eu_substance_number: {
                    "ui:options": {
                      title: "EU substance number",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  atc_code: {
                    "ui:options": {
                      title: "ATC Code",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  atc_name: {
                    "ui:options": {
                      title: "ATC name",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  name: {
                    "ui:options": {
                      title: "Name",
                      label: false,
                      classNames: {
                        fieldContainer: "rjs-col-lg",
                      },
                    },
                  },
                  excluded_msc: {
                    "ui:options": {
                      title: "Excluded MSC",
                      label: false,
                      hideFork: true,
                      classNames: {
                        fieldContainer: "rjs-col-xl",
                      },
                    },
                    "ui:widget": getWidget({ type: "MultiSelect", preview }),
                  },
                  product_properties: {
                    "ui:options": {
                      title: "Details for product",
                      classNames: {
                        objectTitle: "rjs-h2",
                      },
                    },
                    medicinal_product_details: {
                      "ui:options": {
                        title: "Medicinal product details",
                        classNames: {
                          fieldContainer: "rjs-full",
                          objectTitle: "rjs-h3",
                        },
                      },
                      is_specific_paediatric_formulation: {
                        "ui:options": {
                          title: "Is this a specific paediatric formulation?",
                          label: false,
                          required: true,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-xl",
                          },
                        },
                        "ui:widget": getWidget({ type: "radio", preview }),
                      },
                      sponsors_product_code: {
                        "ui:options": {
                          title: "Sponsor's product code",
                          label: false,
                          classNames: {
                            fieldContainer: "rjs-col-xl",
                          },
                        },
                      },
                    },
                    product_characteristics: {
                      "ui:options": {
                        title: "Product characteristics",
                        classNames: {
                          objectTitle: "rjs-h3",
                          fieldContainer: "rjs-full",
                        },
                      },
                      medicinal_product_characteristics: {
                        "ui:options": {
                          title: "Medicinal product characteristics",
                          label: false,
                          classNames: {
                            fieldContainer: "rjs-col-lg",
                          },
                        },
                      },
                      other_medical_product: {
                        "ui:options": {
                          title: "Other medicinal product",
                          label: false,
                          classNames: {
                            fieldContainer: "rjs-col-xl",
                          },
                        },
                      },
                    },
                    dosage_and_administration_details: {
                      "ui:options": {
                        title: "Dosage and administration details",
                        classNames: {
                          objectTitle: "rjs-h3",
                        },
                      },
                      route_of_administration: {
                        "ui:options": {
                          title: "Route of administration",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-2xl",
                          },
                        },
                      },
                      maximum_duration_of_treatmeant: {
                        "ui:options": {
                          title: "Maximum duration of treatment",
                          classNames: {
                            objectTitle: "rjs-h3",
                            fieldContainer: "rjs-full",
                          },
                        },
                        value: {
                          "ui:options": {
                            title: "Duration",
                            required: true,
                            label: false,
                            disableValidation,
                            classNames: {
                              fieldContainer: "rjs-col-sm",
                            },
                          },
                        },
                        duration: {
                          "ui:options": {
                            title: "Type",
                            required: true,
                            label: false,
                            disableValidation,
                            classNames: {
                              fieldContainer: "rjs-col-sm",
                            },
                          },
                        },
                      },
                      maximum_daily_dose_allowed: {
                        "ui:options": {
                          title: "Maximum daily dose allowed",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-md",
                          },
                        },
                      },
                      daily_dose_unit_of_measure: {
                        "ui:options": {
                          title: "Daily dose unit of measure",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-lg",
                          },
                        },
                      },
                      maximum_total_dose_allowed: {
                        "ui:options": {
                          title: "Maximum total dose allowed",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-md",
                          },
                        },
                      },
                      total_dose_use_of_measure: {
                        "ui:options": {
                          title: "Total dose unit of measure",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-lg",
                          },
                        },
                      },
                    },
                    information_about_modification_of_medicinal_product: {
                      "ui:options": {
                        title: "Information about the modification of the Medicinal Product ",
                        classNames: {
                          objectTitle: "rjs-h3",
                          fieldContainer: "rjs-full",
                        },
                      },
                      is_product_modified_in_relation_to_marketing_authorisation: {
                        "ui:options": {
                          title: "Has the medicinal product been modified in relation to its Marketing Authorisation?",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-full",
                          },
                        },
                        "ui:widget": getWidget({ type: "radio", preview }),
                      },
                      description_of_modification: {
                        "ui:options": {
                          title: "Description of the modification",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-4xl",
                          },
                        },
                        "ui:widget": getWidget({ type: "textarea", preview }),
                      },
                    },
                    orphan_designation: {
                      "ui:options": {
                        title: "Orphan Designation",
                        classNames: {
                          objectTitle: "rjs-h3",
                          fieldContainer: "rjs-full",
                        },
                      },
                      product_have_orphan_drug_description: {
                        "ui:options": {
                          title: "Does this product have an orphan drug designation",
                          label: false,
                          classNames: {
                            fieldContainer: "rjs-full",
                          },
                        },
                        "ui:widget": getWidget({ type: "radio", preview }),
                      },
                      designation_number_for_orphan_drug: {
                        "ui:options": {
                          title: "Designation number for orphan drug",
                          required: true,
                          label: false,
                          disableValidation,
                          classNames: {
                            fieldContainer: "rjs-col-4xl",
                          },
                        },
                        "ui:widget": getWidget({ type: "textarea", preview }),
                      },
                    },
                    active_substance: {
                      "ui:options": {
                        title: "Active substance",
                        classNames: {
                          objectTitle: "rjs-h3",
                          fieldContainer: "rjs-full",
                        },
                      },
                      active_substance_other_descriptive_name: {
                        "ui:options": {
                          title: "Active substance other descriptive name",
                          label: false,
                          classNames: {
                            fieldContainer: "rjs-col-4xl",
                          },
                        },
                        "ui:widget": getWidget({ type: "textarea", preview }),
                      },
                    },
                    advanced_therapy_medicinal_product: {
                      "ui:options": {
                        title: "Advanced Therapy Medicinal Product",
                        classNames: {
                          objectTitle: "rjs-h3",
                          fieldContainer: "rjs-full",
                        },
                      },
                      advanced_therapy: {
                        "ui:options": {
                          title: "Advanced Therapy",
                          addButtonTitle: "Add Advanced Therapy",
                          arrayItemTitle: "Advanced Therapy",
                          showIndex: true,
                          label: false,
                          addable: true,
                          removable: true,
                          orderable: true,
                          classNames: arrayClassNames.secondary,
                        },
                        items: {
                          advanced_therapy_classification: {
                            "ui:options": {
                              title: "Advanced therapy classification",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                          },
                          cat_reference_number: {
                            "ui:options": {
                              title: "CAT reference number",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                          },
                          somatic_cell_origin: {
                            "ui:options": {
                              title: "Somatic cell origin",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          somatic_cell_type: {
                            "ui:options": {
                              title: "Somatic cell type",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          species_origin_xenogeneic_cell: {
                            "ui:options": {
                              title: "Species origin for the xenogeneic cell",
                              label: false,
                              required: true,
                              disableValidation,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          specify_type_differentiated_cells: {
                            "ui:options": {
                              title: "Specify type of differentiated cells",
                              label: false,
                              required: true,
                              disableValidation,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          tissue_engineered_cell_type: {
                            "ui:options": {
                              title: "Tissue engineered cell type",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          origin_of_engineered_tissue: {
                            "ui:options": {
                              title: "Origin of the engineered tissue",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          cell_specification: {
                            "ui:options": {
                              title: "Cell specification",
                              label: false,
                              required: true,
                              disableValidation,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          tissue_engineered_xenogeneic_species_origin: {
                            "ui:options": {
                              title: "Tissue Engineered xenogeneic species of origin",
                              label: false,
                              required: true,
                              disableValidation,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          gene_interest: {
                            "ui:options": {
                              title: "Gene of interest",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                          },
                          gene_therapy_type: {
                            "ui:options": {
                              title: "Type of gene transfer product",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          type_of_gene_transfer_product: {
                            "ui:options": {
                              title: "Gene therapy type",
                              label: false,
                              required: true,
                              disableValidation,
                              classNames: {
                                fieldContainer: "rjs-col-md",
                              },
                            },
                          },
                          genetically_modified_cell_present: {
                            "ui:options": {
                              title: "Genetically modified cells present?",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                            "ui:widget": getWidget({ type: "radio", preview }),
                          },
                          additional_description: {
                            "ui:options": {
                              title: "Additional description",
                              label: false,
                              required: true,
                              disableValidation,
                              classNames: {
                                fieldContainer: "rjs-col-4xl",
                              },
                            },
                            "ui:widget": getWidget({ type: "textarea", preview }),
                          },
                          specify_type_of_cells: {
                            "ui:options": {
                              title: "Specify type of cells",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                          },
                          origin_of_genetically_modiefied_cells: {
                            "ui:options": {
                              title: "Origin of the genetically modified cells",
                              label: false,
                              required: true,
                              disableValidation,
                              classNames: {
                                fieldContainer: "rjs-col-xl",
                                field: "rjs-col-lg",
                              },
                            },
                          },
                        },
                      },
                    },
                    device_associated_with_medicinal_product: {
                      "ui:options": {
                        title: "Device associated with medicinal product",
                        classNames: {
                          objectTitle: "rjs-h3",
                          fieldContainer: "rjs-full",
                        },
                      },
                      device: {
                        "ui:options": {
                          title: "Device",
                          addButtonTitle: "Add Device",
                          arrayItemTitle: "Device",
                          showIndex: true,
                          label: false,
                          addable: true,
                          removable: true,
                          orderable: true,
                          classNames: arrayClassNames.secondary,
                        },
                        items: {
                          product_used_in_combination_with_device: {
                            "ui:options": {
                              title: "Product used in combination with a device",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                          },
                          device_type: {
                            "ui:options": {
                              title: "Type of device",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                          },
                          device_has_ce_mark: {
                            "ui:options": {
                              title: "Device has CE mark",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-col-lg",
                              },
                            },
                            "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
                          },
                          device_trade_name: {
                            "ui:options": {
                              title: "Device Trade Name",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-full",
                              },
                            },
                            "ui:widget": getWidget({ type: "textarea", preview }),
                          },
                          device_notified_body: {
                            "ui:options": {
                              title: "Device notified body",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-full",
                              },
                            },
                            "ui:widget": getWidget({ type: "textarea", preview }),
                          },
                          description_of_device: {
                            "ui:options": {
                              title: "Description of the device",
                              label: false,
                              classNames: {
                                fieldContainer: "rjs-full",
                              },
                            },
                            "ui:widget": getWidget({ type: "textarea", preview }),
                          },
                        },
                      },
                    },
                  },
                },
              },
              description: {
                "ui:options": {
                  title: "Description of the Placebo:*",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "textarea", preview }),
              },
              authorisation_number_manufacturing_and_import: {
                "ui:options": {
                  title: "Authorisation number of manufacturing and import",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "textarea", preview }),
              },
              quality_justification_for_no_impd_upload: {
                "ui:options": {
                  title: "IMPD - Quality: Justification for no IMPD upload",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                  "ui:widget": getWidget({ type: "textarea", preview }),
                },
                "ui:widget": getWidget({ type: "textarea", preview }),
              },
              safety_and_efficacy_justification_for_no_impd_upload: {
                "ui:options": {
                  title: "IMPD - Safety and Efficacy: Justification for no IMPD upload",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "textarea", preview }),
              },
              reason_for_no_ampd_upload: {
                "ui:options": {
                  title: "Reason for no AMPD upload",
                  label: false,
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
              },
            },
          },
          ...setHiddenFields(hideFields.study_description.products.root),
        },
        ...setHiddenFields(hideFields.study_description.root),
      },
      study_scope: {
        "ui:options": {
          title: "Countries outside the European Economic Area",
          classNames: objectClassNames.primary,
        },
        "ui:order": ["countries_outside_eea", "enrollment_number", "*"],
        countries_outside_eea: {
          "ui:options": {
            title: "Countries outside the European Economic Area",
            label: false,
            hideFork: true,
            classNames: {
              fieldContainer: "rjs-col-2xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        enrollment_number: {
          "ui:options": {
            title: "Rest of the world subjects*",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
        },
        ...setHiddenFields(hideFields.study_scope.root),
      },
      study_eligibilty: {
        "ui:options": {
          title: "Eligibility Criteria",
          classNames: objectClassNames.primary,
        },
        sex: {
          "ui:options": {
            title: "Gender",
            placeholder: "--Select--",
            errorMessage: "Gender has not been selected.",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        population_under_study: {
          "ui:options": {
            title: "Population under study",
            label: false,
            classNames: objectClassNames.tertiary,
          },
          age_range_ctis: {
            "ui:options": {
              title: "Age range",
              label: false,
              hideFork: true,
              classNames: {
                fieldContainer: "rjs-col-md",
              },
            },
            "ui:widget": getWidget({ type: "MultiSelect", preview }),
          },
          age_range_secondary_ctis: {
            "ui:options": {
              title: "Age range secondary identifier",
              label: false,
              hideFork: true,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
            "ui:widget": getWidget({ type: "MultiSelect", preview }),
          },
          clinical_trial_group: {
            "ui:options": {
              title: "Clinical trial group",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "MultiSelect", preview }),
          },
          vulnerable_population: {
            "ui:options": {
              title: "Vulnerable population",
              required: true,
              label: false,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
          },
          recruitment_population_group: {
            "ui:options": {
              title: "Recruitment population group",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "MultiSelect", preview }),
          },
          subjects_incapable_giving_consent: {
            "ui:options": {
              title: "Subjects incapable of giving consent personally",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "MultiSelect", preview }),
          },
          emergency_situation_description: {
            "ui:options": {
              title: "Emergency situation description",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },
          other_description: {
            "ui:options": {
              title: "Other description",
              label: false,
              required: true,
              disableValidation,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },

          ...setHiddenFields(hideFields.study_eligibilty.population_under_study.root),
        },
        ...setHiddenFields(hideFields.study_eligibilty.root),
      },
      study_ipd: {
        "ui:options": {
          title: "IPD Sharing Statements",
          classNames: objectClassNames.primary,
        },
        plan_to_share_IPD: {
          ...ctgSchema.protocol.study_ipd.plan_to_share_IPD,
          "ui:options": {
            ...ctgSchema.protocol.study_ipd.plan_to_share_IPD["ui:options"],
            required: true,
          },
        },
        plan_description: ctgSchema.protocol.study_ipd.plan_description,
        ...setHiddenFields(hideFields.study_ipd.root),
      },
      study_reference: {
        "ui:options": {
          title: "References",
          classNames: objectClassNames.primary,
        },
        study_publications: {
          "ui:options": {
            title: "Online references",
            addButtonTitle: "Add link",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            reference: {
              "ui:options": {
                title: "Reference to publication",
                label: false,
                hideFork: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
            ...setHiddenFields(hideFields.study_reference.study_publications.root),
          },
        },
        ...setHiddenFields(hideFields.study_reference.root),
      },
      sponsors: {
        "ui:options": {
          title: "Sponsors",
          classNames: objectClassNames.primary,
        },
        sponsors: {
          "ui:options": {
            title: "Sponsors",
            itemTitle: "Sponsor",
            addButtonTitle: "Add Sponsor",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            "ui:order": [
              "sponsor",
              "add_legal_representative",
              "legal_representative",
              "add_scientific_contact_point",
              "scientific_contact_point",
              "add_public_contact_point",
              "public_contact_point",
              "*",
            ],
            sponsor: {
              "ui:options": {
                title: "Sponsor",
                classNames: {
                  objectTitle: "rjs-h3",
                },
              },
              organisation_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Name",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              status: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Status",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              address: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Address",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              city: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Town/City",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              zip_code: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Post code",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              country: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Country",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              email: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Email",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              phone: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Phone",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
            add_legal_representative: {
              "ui:options": {
                title: "Add legal representative",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
            },
            legal_representative: {
              "ui:options": {
                title: "Legal representative",
                classNames: {
                  objectTitle: "rjs-h3",
                },
              },
              organisation_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Name",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              status: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Status",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              address: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Address",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              city: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Town/City",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              zip_code: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Post code",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              country: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Country",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              email: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Email",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              phone: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Phone",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              contact: {
                "ui:options": {
                  title: "Contact",
                  classNames: {
                    objectTitle: "rjs-h3",
                  },
                },
                first_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "First Name",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                last_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Last Name",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                email: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Email",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                phone: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Phone",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
              },
            },
            add_scientific_contact_point: {
              "ui:options": {
                title: "Add scientific contact point",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
            },
            scientific_contact_point: {
              "ui:options": {
                title: "Scientific contact point",
                classNames: {
                  objectTitle: "rjs-h3",
                },
              },
              organisation_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Name",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              functional_contact_point_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Status",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              email: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Email",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              phone: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Phone",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
            add_public_contact_point: {
              "ui:options": {
                title: "Add public contact point",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "SingleCheckbox", preview }),
            },
            public_contact_point: {
              "ui:options": {
                title: "Public contact point",
                classNames: {
                  objectTitle: "rjs-h3",
                },
              },
              organisation_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Name",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              functional_contact_point_name: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Status",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              email: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Email",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              phone: {
                "ui:options": {
                  label: false,
                  required: true,
                  disableValidation,
                  title: "Phone",
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
            third_party: {
              "ui:options": {
                title: "Third party",
                itemTitle: "Sponsor",
                addButtonTitle: "Add third party",
                label: false,
                addable: true,
                removable: true,
                orderable: true,
                classNames: arrayClassNames.primary,
              },
              items: {
                organisation_name: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Name",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                country: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Country",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                address: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Address",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                city: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Town/City",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                zip_code: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Post code",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                phone: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Phone",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                email: {
                  "ui:options": {
                    label: false,
                    required: true,
                    disableValidation,
                    title: "Email",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                duties: {
                  "ui:options": {
                    title: "Duties",
                    label: false,
                    orientation: "vertical",
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                  "ui:widget": getWidget({ type: "MultiSelect", preview }),
                },
                other_duties: {
                  "ui:options": {
                    title: "Please explain if other duties: ",
                    label: false,
                    orientation: "vertical",
                    classNames: {
                      fieldContainer: "rjs-full",
                    },
                  },
                  "ui:widget": getWidget({ type: "textarea", preview }),
                },
              },
            },
          },
        },
        contact_point_for_union: {
          "ui:options": {
            title: "Contact point for union",
            classNames: {
              objectTitle: "rjs-h3",
            },
          },
          organisation_name: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Name",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          address_1: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Address 1",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          address_2: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Address 2",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          address_3: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Address 3",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          address_4: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Address 4",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          city: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Town/City",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          zip_code: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Post code",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          country: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Country",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          functional_contact_point_name: {
            "ui:options": {
              label: false,
              required: true,
              disableValidation,
              title: "Functional contact point name",
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          contact: {
            "ui:options": {
              title: "Contact",
              classNames: {
                objectTitle: "rjs-h3",
              },
            },
            first_name: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "First name",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            last_name: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Last name",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            phone: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Phone",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            email: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "Email",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
          },
        },
      },
      milestone_dates: {
        "ui:options": {
          classNames: {
            fieldContainer: "rjs-full",
            objectContainer: "rjs-full",
          },
        },
        lpo: {
          "ui:options": {
            title: "Estimated global end of trial date",
            classNames: {
              ...objectClassNames.tertiary,
            },
          },
          forecast: {
            "ui:options": {
              title: "Forecast",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": "date",
          },
          ...setHiddenFields(hideFields.study_details.milestone_dates.lpo.root),
        },
        ...setHiddenFields(hideStudyDetailsKeys.milestone_dates.root),
        ...setHiddenFields(hideFields.study_details.milestone_dates.root),
      },
      secondary_attributes: {
        "ui:options": {
          title: "Study Timelines and Status",
          classNames: objectClassNames.primary,
        },
        europe: {
          "ui:options": {
            title: "Milestone dates",
            classNames: {
              ...objectClassNames.tertiary,
            },
          },
          lpo_eu_eea: {
            "ui:options": {
              title: "Estimated end of trial date in EEA",
              classNames: objectClassNames.tertiary,
            },
            forecast: {
              "ui:options": {
                title: "Forecast",
                label: false,
                classNames: {
                  field: "rjs-col-date",
                },
              },
              "ui:widget": "date",
            },
            ...setHiddenFields(hideStudyDetailsKeys.secondary_attributes.europe.lpo_eu_eea.root),
          },
          ...setHiddenFields(hideStudyDetailsKeys.secondary_attributes.europe.root),
        },
        ...setHiddenFields(hideStudyDetailsKeys.secondary_attributes.root),
      },
      ...setHiddenFields(hideStudyDetailsKeys.root),
      ...setHiddenFields(hideFields.root),
    },
  }

  if (preview === true) {
    getPreviewStyles(returnObj, formData)
  }

  return returnObj
}
